import React from "react"
import { useNavigate } from "react-router-dom"
import { observer, useLocalObservable } from "mobx-react"
import { Flex } from "antd"
import { choose } from "choose-matching"

import { balancesStore } from "@src/app/balanceStore"
import { paths } from "@src/app/paths"
import {
  CardsPayoutFormValue,
  MassPayoutsCardCollection,
} from "@src/pages/massPayouts/cards/classes/MassPayoutsCardCollection"
import { MassPayoutCardsBalances } from "@src/pages/massPayouts/cards/ui/MassPayoutCardsBalances"
import { MassPayoutCardsForm } from "@src/pages/massPayouts/cards/ui/MassPayoutCardsForm"
import { MassPayoutCardsFormUpload } from "@src/pages/massPayouts/cards/ui/MassPayoutCardsFormUpload"
import { MassPayoutCardsTable } from "@src/pages/massPayouts/cards/ui/MassPayoutCardsTable"
import { MassPayoutsPageView } from "@src/pages/massPayouts/crypto"
import { GlobalPayoutsConformationProcess } from "@src/pages/payoutsConfirmation/classes/PayoutsConformationProcess"
import { CardMassPayoutResponse } from "@src/shared/api/generated"

type Props = {
  view: MassPayoutsPageView
}

export const MassPayoutsCardsPage = observer(({ view }: Props) => {
  const collection = useLocalObservable(() => new MassPayoutsCardCollection())
  const balances = balancesStore

  const nav = useNavigate()

  const toConfirm = (data: CardMassPayoutResponse) => {
    nav(paths.payoutsConfirmation)

    GlobalPayoutsConformationProcess().setState({
      id: data.id,
      payouts: data.payouts,
      type: "card",
    })
  }

  const csv = async (v: Blob) => {
    const data = await collection.createFromCsv(v)

    if (data) {
      toConfirm(data)
    }
  }

  const manual = async ({ payouts }: CardsPayoutFormValue) => {
    const data = await collection.createManual({ payouts })

    if (data) {
      toConfirm(data)
    }
  }

  return (
    <Flex gap={24} vertical>
      <MassPayoutCardsBalances balances={balances.cards} />

      {choose(
        [
          view === MassPayoutsPageView.MANUAL,
          <MassPayoutCardsForm
            currencies={balances.cards}
            isLoading={collection.isLoading}
            sendFunds={manual}
          />,
        ],
        [
          view === MassPayoutsPageView.UPLOAD,
          <MassPayoutCardsFormUpload
            isLoading={collection.isLoading}
            sendFunds={csv}
          />,
        ],
      )}

      <MassPayoutCardsTable collection={collection} />
    </Flex>
  )
})
